<template>
  <div class="movie-detailed-view min-h-screen bg-background/50 dark:bg-background/80">
    <BubbleHeader :bubble="bubble" />
    <div class="max-w-7xl mx-auto p-4 sm:p-6 space-y-8">
      <!-- Hero Section -->
      <div
        class="relative overflow-hidden bg-gradient-to-br from-card to-card/50 rounded-xl shadow-lg dark:from-card/80 dark:to-card/30"
      >
        <div
          class="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/0 dark:from-primary/10 dark:to-primary/0"
        ></div>
        <div class="relative flex flex-col md:flex-row p-6 sm:p-8 gap-8">
          <div class="relative md:w-1/3 flex-shrink-0">
            <div
              class="aspect-[2/3] rounded-lg overflow-hidden shadow-xl ring-1 ring-black/5 dark:ring-white/10"
            >
              <NuxtImg
                v-if="posterSrc"
                :src="posterSrc"
                :alt="bubble.title"
                class="w-full h-full object-cover transform transition-transform duration-300 hover:scale-105"
              />
            </div>
          </div>

          <div class="md:w-2/3 flex flex-col justify-between">
            <div class="space-y-6">
              <h1 class="text-4xl font-bold tracking-tight">{{ bubble.title }}</h1>

              <div class="flex flex-wrap gap-3">
                <span
                  class="px-3 py-1 bg-primary/5 hover:bg-primary/10 dark:bg-primary/10 dark:hover:bg-primary/15 text-primary/80 dark:text-primary-foreground/80 rounded-full text-sm font-medium transition-colors ring-1 ring-primary/10 dark:ring-primary/20"
                >
                  {{ bubble.year }}
                </span>
                <span
                  v-if="bubble.runtime"
                  class="px-3 py-1 bg-primary/5 hover:bg-primary/10 dark:bg-primary/10 dark:hover:bg-primary/15 text-primary/80 dark:text-primary-foreground/80 rounded-full text-sm font-medium transition-colors ring-1 ring-primary/10 dark:ring-primary/20"
                >
                  {{ bubble.runtime }} min
                </span>
                <span
                  v-if="bubble.rated"
                  class="px-3 py-1 bg-primary/5 hover:bg-primary/10 dark:bg-primary/10 dark:hover:bg-primary/15 text-primary/80 dark:text-primary-foreground/80 rounded-full text-sm font-medium transition-colors ring-1 ring-primary/10 dark:ring-primary/20"
                >
                  {{ bubble.rated }}
                </span>
              </div>

              <p v-if="bubble.plot" class="text-lg text-muted-foreground leading-relaxed">
                {{ bubble.plot }}
              </p>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div
                  v-if="bubble.director && bubble.director.length > 0"
                  class="flex items-center gap-3"
                >
                  <div class="p-2 rounded-full bg-muted/50 dark:bg-muted/20">
                    <Icon name="heroicons:video-camera" class="w-5 h-5 text-primary" />
                  </div>
                  <span class="text-sm"
                    >Directed by
                    <span class="font-medium">{{ bubble.director.join(', ') }}</span></span
                  >
                </div>
                <div v-if="bubble.genre" class="flex items-center gap-3">
                  <div class="p-2 rounded-full bg-muted/50 dark:bg-muted/20">
                    <Icon name="heroicons:tag" class="w-5 h-5 text-primary" />
                  </div>
                  <span class="text-sm">{{ bubble.genre }}</span>
                </div>
              </div>
            </div>

            <div class="flex flex-wrap gap-6 mt-8">
              <div
                v-if="bubble.imdbRating"
                class="group flex items-center gap-3 p-3 rounded-lg bg-card/50 dark:bg-card/30 transition-colors duration-200 hover:bg-card"
              >
                <img
                  src="/imdb.png"
                  alt="IMDb"
                  class="w-12 transition-transform duration-200 group-hover:scale-110"
                />
                <div>
                  <div class="font-bold text-xl">
                    {{ bubble.imdbRating }}<span class="text-sm text-muted-foreground">/10</span>
                  </div>
                  <div class="text-xs text-muted-foreground">{{ bubble.imdbVotes }} votes</div>
                </div>
              </div>

              <div
                v-if="bubble.rottenTomatoes"
                class="group flex items-center gap-3 p-3 rounded-lg bg-card/50 dark:bg-card/30 transition-colors duration-200 hover:bg-card"
              >
                <img
                  src="/rotten.png"
                  alt="Rotten Tomatoes"
                  class="w-12 transition-transform duration-200 group-hover:scale-110"
                />
                <div class="font-bold text-xl">
                  {{ bubble.rottenTomatoes }}<span class="text-sm text-muted-foreground">%</span>
                </div>
              </div>

              <div
                v-if="bubble.metascore"
                class="group flex items-center gap-3 p-3 rounded-lg bg-card/50 dark:bg-card/30 transition-colors duration-200 hover:bg-card"
              >
                <img
                  src="/meta.png"
                  alt="Metacritic"
                  class="w-12 transition-transform duration-200 group-hover:scale-110"
                />
                <div class="font-bold text-xl">{{ bubble.metascore }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Details Section -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div
          class="bg-card dark:bg-card/50 rounded-xl shadow-lg p-6 transition-all duration-200 hover:shadow-xl"
        >
          <h2 class="text-xl font-semibold mb-6 flex items-center gap-2">
            <Icon name="heroicons:users" class="w-5 h-5 text-primary" />
            Cast & Crew
          </h2>

          <div v-if="bubble.actors && bubble.actors.length > 0" class="mb-8">
            <h3 class="font-medium text-muted-foreground mb-3">Cast</h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="actor in bubble.actors"
                :key="actor"
                class="px-3 py-1.5 bg-muted/30 hover:bg-muted/50 dark:bg-muted/10 dark:hover:bg-muted/20 text-foreground/80 dark:text-foreground/70 rounded-full text-sm font-medium transition-colors duration-200"
              >
                {{ actor }}
              </span>
            </div>
          </div>

          <div v-if="bubble.writer" class="mb-6">
            <h3 class="font-medium text-muted-foreground mb-3">Writers</h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="writer in bubble.writer"
                :key="writer"
                class="px-3 py-1.5 bg-muted/30 hover:bg-muted/50 dark:bg-muted/10 dark:hover:bg-muted/20 text-foreground/80 dark:text-foreground/70 rounded-full text-sm font-medium transition-colors duration-200"
              >
                {{ writer }}
              </span>
            </div>
          </div>
        </div>

        <div
          class="bg-card dark:bg-card/50 rounded-xl shadow-lg p-6 transition-all duration-200 hover:shadow-xl"
        >
          <h2 class="text-xl font-semibold mb-6 flex items-center gap-2">
            <Icon name="heroicons:information-circle" class="w-5 h-5 text-primary" />
            Additional Details
          </h2>

          <div v-if="bubble.awards" class="mb-6">
            <h3 class="font-medium text-muted-foreground mb-2">Awards</h3>
            <p class="text-sm leading-relaxed">{{ bubble.awards }}</p>
          </div>

          <div v-if="bubble.boxOffice" class="mb-6">
            <h3 class="font-medium text-muted-foreground mb-2">Box Office</h3>
            <p class="text-sm">{{ bubble.boxOffice }}</p>
          </div>

          <div class="grid grid-cols-2 gap-6">
            <div v-if="bubble.language">
              <h3 class="font-medium text-muted-foreground mb-2">Language</h3>
              <p class="text-sm">{{ bubble.language }}</p>
            </div>
            <div v-if="bubble.country">
              <h3 class="font-medium text-muted-foreground mb-2">Country</h3>
              <p class="text-sm">{{ bubble.country }}</p>
            </div>
            <div v-if="bubble.releaseDate">
              <h3 class="font-medium text-muted-foreground mb-2">Release Date</h3>
              <p class="text-sm">{{ formatDate(bubble.releaseDate) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Section -->
      <div
        class="bg-card dark:bg-card/50 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
      >
        <div class="p-6 border-b border-border dark:border-border/50">
          <h2 class="text-xl font-semibold flex items-center gap-2">
            <Icon name="heroicons:chart-bar" class="w-5 h-5 text-primary" />
            Your Stats
          </h2>
        </div>
        <BubbleStats
          :bubble-uuid="bubble.uuid"
          did-label="Watched"
          placeholder="Write your thoughts about this movie..."
        />
      </div>

      <!-- External Links -->
      <div class="flex flex-wrap gap-4">
        <a
          v-if="bubble.website"
          :href="bubble.website"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-2 px-4 py-2 bg-card dark:bg-card/50 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group"
        >
          <Icon
            name="heroicons:globe-alt"
            class="w-5 h-5 text-primary transition-transform duration-200 group-hover:scale-110"
          />
          <span class="font-medium">Official Website</span>
        </a>
        <a
          v-if="bubble.imdbId"
          :href="`https://www.imdb.com/title/${bubble.imdbId}`"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-2 px-4 py-2 bg-card dark:bg-card/50 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group"
        >
          <Icon
            name="simple-icons:imdb"
            class="w-5 h-5 text-primary transition-transform duration-200 group-hover:scale-110"
          />
          <span class="font-medium">View on IMDb</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MovieBubble } from '@/stores/interfaces'
import BubbleHeader from '../BubbleHeader.vue'
import { computed } from 'vue'
import { useRuntimeConfig } from '#app'
import BubbleStats from '../BubbleStats.vue'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

const {
  public: { dataStore },
} = useRuntimeConfig()

const props = defineProps<{
  bubble: MovieBubble
}>()

// Use the type navigation composable
useTypeNavigation(props.bubble)

const formatDate = (dateString: string | null): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const posterSrc = computed(() => {
  if (props.bubble.offline && props.bubble.offlineMediaResources?.poster) {
    return `${dataStore}${props.bubble.offlineMediaResources.poster}`
  }
  return props.bubble.poster
})
</script>

<style scoped>
.movie-detailed-view {
  background-image:
    radial-gradient(circle at top left, var(--primary-50) 0%, transparent 20%),
    radial-gradient(circle at bottom right, var(--primary-50) 0%, transparent 20%);
}

@media (prefers-color-scheme: dark) {
  .movie-detailed-view {
    background-image:
      radial-gradient(circle at top left, var(--primary-900) 0%, transparent 25%),
      radial-gradient(circle at bottom right, var(--primary-900) 0%, transparent 25%);
  }
}
</style>
